import React, { useEffect, useRef, useState } from 'react';
import './MapModal.css';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { fetchPlaceSuggestions } from './api';

function MapModal({ 
  currentLanguage, 
  getLocalizedText, 
  onClose, 
  onSelectLocation,
  initialPosition = null   // ← prop جديد
}) {
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const searchBarRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const debounceTimeout = useRef(null);

  // إعدادات الخريطة الافتراضية (القاهرة)
  const defaultPosition = [30.0444, 31.2357]; 

  // دالة بسيطة لاكتشاف لغة النص اللي المستخدم كاتبه
  // لو فيه حروف عربي تعتبر النص عربي، غير كده انجليزي
  const detectQueryLanguage = (text) => {
    const arabicRegex = /[\u0600-\u06FF]/;
    return arabicRegex.test(text) ? 'ar' : 'en';
  };

  useEffect(() => {
    // تحديد نقطة البداية بناءً على الـ prop الجديد
    const startPosition = initialPosition 
      ? [initialPosition.lat, initialPosition.lng] 
      : defaultPosition;

    // تهيئة الخريطة
    mapInstanceRef.current = L.map(mapRef.current, {
      center: startPosition,
      zoom: initialPosition ? 16 : 12, // زوم أكبر لو جاي من feature محدد
      scrollWheelZoom: true,
      zoomControl: false,
    });

    // إضافة Zoom Control يدويًا بناءً على اللغة
    L.control
      .zoom({
        position: currentLanguage === 'ar' ? 'topright' : 'topleft',
      })
      .addTo(mapInstanceRef.current);

    // إضافة طبقة OpenStreetMap
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(mapInstanceRef.current);

    // إعداد أيقونة الماركر
    const markerIcon = L.divIcon({
      className: 'custom-marker',
      html: '<div style="background-color: #e00000; width: 12px; height: 12px; border-radius: 50%; border: 1px solid #000;"></div>',
      iconSize: [16, 16],
      iconAnchor: [8, 8],
    });

    // إضافة ماركر في موضع البداية
    markerRef.current = L.marker(startPosition, {
      icon: markerIcon,
      draggable: false,
    }).addTo(mapInstanceRef.current);

    // تحديث موقع الماركر عند تحريك الخريطة
    mapInstanceRef.current.on('move', () => {
      const center = mapInstanceRef.current.getCenter();
      markerRef.current.setLatLng(center);
    });

    // إغلاق قائمة الاقتراحات عند النقر خارج شريط البحث
    const handleClickOutside = (event) => {
      if (searchBarRef.current && !searchBarRef.current.contains(event.target)) {
        setIsSearchDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);

    // تنظيف الخريطة عند إغلاق المكون
    return () => {
      document.removeEventListener('click', handleClickOutside);
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
      }
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [currentLanguage, initialPosition]); // إضافة initialPosition كـ dependency

  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setErrorMessage('');

    if (query.length < 4) {
      setSuggestions([]);
      setIsSearchDropdownOpen(false);
      return;
    }

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(async () => {
      try {
        const queryLang = detectQueryLanguage(query);
        const results = await fetchPlaceSuggestions(query, null, queryLang);
        setSuggestions(results.slice(0, 3));
        setIsSearchDropdownOpen(true);
      } catch (error) {
        setErrorMessage(getLocalizedText('خطأ في جلب الاقتراحات', 'Error fetching suggestions'));
        setSuggestions([]);
        setIsSearchDropdownOpen(true);
      }
    }, 500);
  };

  const handleSuggestionSelect = (suggestion) => {
    const { lat, lon } = suggestion.properties;
    mapInstanceRef.current.setView([lat, lon], 15);
    markerRef.current.setLatLng([lat, lon]);
    setSearchQuery(suggestion.properties.formatted);
    setIsSearchDropdownOpen(false);
  };

  const handleSetLocation = async () => {
    const center = mapInstanceRef.current.getCenter();
    const lat = center.lat;
    const lng = center.lng;

    try {
      const response = await axios.get('https://api.geoapify.com/v1/geocode/reverse', {
        params: {
          lat,
          lon: lng,
          apiKey: '78b3972dccf34fba862bc72d8b73c7ad',
          lang: 'en',
        },
      });

     const address = response.data.features[0]?.properties?.formatted || 'Unknown address';
      
      onSelectLocation({ 
        address: address, 
        lat: lat, 
        lng: lng 
      });
      
      onClose();
    } catch (error) {
      console.error('Error fetching address:', error);
      onSelectLocation({ 
        address: 'Error fetching address', 
        lat: lat, 
        lng: lng 
      });
      onClose();
    }
  };

  return (
    <div className="map-overlay">
      <div className="map-container">
        <span className="close-button" onClick={onClose}>×</span>
        <div className="new-search-bar-wrapper" ref={searchBarRef}>
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchInputChange}
            placeholder={getLocalizedText('ادخل العنوان', 'Enter address')}
            className="new-search-input"
            dir={currentLanguage === 'ar' ? 'rtl' : 'ltr'}
          />
          {isSearchDropdownOpen && (
            <div className="search-dropdown-menu">
              {errorMessage ? (
                <div className="search-dropdown-item">{errorMessage}</div>
              ) : suggestions.length === 0 && searchQuery.length >= 4 ? (
                <div className="search-dropdown-item">{getLocalizedText('لا توجد نتائج', 'No results found')}</div>
              ) : (
                suggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    className="search-dropdown-item"
                    onClick={() => handleSuggestionSelect(suggestion)}
                  >
                    {suggestion.properties.formatted}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
        <div className="map-content" ref={mapRef}>
          <button className="set-location-button" onClick={handleSetLocation}>
            {getLocalizedText('تأكيد الموقع', 'Set Location')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default MapModal;